// /fornecedores/:id/compras — produtos ja comprados deste fornecedor.
// Fonte EXCLUSIVA: notas de entrada (nota fiscal de compra) — nao mistura cotacao.
// 1 linha por produto, com ultimo preco/embalagem/data + totais acumulados.
// Espelho do /clientes/:id/extrato, virado pro lado da compra.
import { useEffect, useMemo, useState } from 'react';
import { upperMantendoCursor } from '../../components/inputs/UpperInput';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, ImageOff, Search } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { formatMoeda, getNomeFornecedor } from '@fiodecobre/shared';

interface FornecedorResumo {
  id: number;
  razao_social: string | null;
  nome_fantasia: string | null;
  cpf_cnpj: string | null;
  cidade: string | null;
  uf: string | null;
}

interface CompraLinha {
  id: string;
  produto_id: number | null;
  produto_codigo_interno: string | null;
  produto_descricao: string | null;
  produto_foto_url: string | null;
  marca: string | null;
  codigo_fornecedor: string | null;
  descricao_nf: string | null;
  embalagem: string | null;
  ncm: string | null;
  ultima_quantidade: string | number | null;
  ultimo_preco: string | number | null;
  ultima_nota_numero: string | null;
  qtd_compras: string | number | null;
  total_quantidade: string | number | null;
  total_gasto: string | number | null;
  ultima_data: string | null;
}

interface ComprasResp {
  fornecedor_id: number;
  total_produtos: number;
  total_gasto: number;
  linhas: CompraLinha[];
}

function fmtQtd(v: string | number | null): string {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', { maximumFractionDigits: 4 });
}

export function ComprasFornecedor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const fornecedorId = Number(id);

  const [fornecedor, setFornecedor] = useState<FornecedorResumo | null>(null);
  const [dados, setDados] = useState<ComprasResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');

  useEffect(() => {
    if (!fornecedorId) return;
    let cancelado = false;
    setLoading(true);
    Promise.all([
      api.get<FornecedorResumo>(`/api/fornecedores/${fornecedorId}`),
      api.get<ComprasResp>(`/api/fornecedores/${fornecedorId}/compras`),
    ])
      .then(([f, c]) => { if (!cancelado) { setFornecedor(f); setDados(c); } })
      .catch((err) => { if (!cancelado) toast.erro((err as Error).message); })
      .finally(() => { if (!cancelado) setLoading(false); });
    return () => { cancelado = true; };
  }, [fornecedorId, toast]);

  const linhasFiltradas = useMemo(() => {
    if (!dados) return [];
    const q = busca.trim().toUpperCase();
    if (!q) return dados.linhas;
    return dados.linhas.filter((l) => {
      const campos = [
        l.produto_codigo_interno, l.codigo_fornecedor, l.produto_descricao,
        l.descricao_nf, l.marca, l.ncm,
      ];
      return campos.some((c) => (c ?? '').toUpperCase().includes(q));
    });
  }, [dados, busca]);

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
        <Loader2 size={20} className="animate-spin" style={{ display: 'inline-block' }} />
      </div>
    );
  }

  if (!fornecedor) {
    return <div style={{ padding: 40, textAlign: 'center' }}>Fornecedor nao encontrado</div>;
  }

  return (
    <div className="pagina">
      <div className="pagina-header" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button type="button" onClick={() => navigate(-1)} title="Voltar" className="btn-icon" style={{ padding: 6 }}>
          <ArrowLeft size={18} />
        </button>
        <div style={{ flex: 1 }}>
          <h2 className="pagina-titulo" style={{ margin: 0 }}>
            Compras — {getNomeFornecedor(fornecedor)}
          </h2>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
            {fornecedor.razao_social && fornecedor.razao_social !== fornecedor.nome_fantasia && (
              <span>{fornecedor.razao_social} · </span>
            )}
            {fornecedor.cpf_cnpj && <span>{fornecedor.cpf_cnpj} · </span>}
            {fornecedor.cidade && fornecedor.uf && <span>{fornecedor.cidade}/{fornecedor.uf}</span>}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>TOTAL COMPRADO (NF)</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--primary)' }}>
            {formatMoeda(dados?.total_gasto ?? 0)}
          </div>
        </div>
      </div>

      {/* Busca + contador */}
      <div style={{
        background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8,
        padding: 12, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(upperMantendoCursor(e))}
            placeholder="BUSCAR POR CODIGO, DESCRICAO, MARCA OU NCM..."
            style={{
              width: '100%', padding: '8px 10px 8px 34px', border: '1px solid var(--input-border)',
              borderRadius: 6, fontSize: 13, background: 'var(--input-bg)', color: 'var(--text-color)',
              textTransform: 'uppercase',
            }}
          />
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
          {busca ? `${linhasFiltradas.length} de ${dados?.total_produtos ?? 0}` : `${dados?.total_produtos ?? 0}`} produtos
        </div>
      </div>

      {/* Tabela */}
      {linhasFiltradas.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
          {dados && dados.total_produtos === 0
            ? 'Nenhuma compra registrada deste fornecedor (via nota de entrada).'
            : 'Nenhum produto encontrado pra essa busca.'}
        </div>
      ) : (
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--table-header-bg, #f3f4f6)', textAlign: 'left' }}>
                <th style={{ padding: '8px 10px', width: 44 }}></th>
                <th style={{ padding: '8px 10px', fontWeight: 600, whiteSpace: 'nowrap' }}>Código</th>
                <th style={{ padding: '8px 10px', fontWeight: 600 }}>Descrição</th>
                <th style={{ padding: '8px 10px', fontWeight: 600 }}>Marca</th>
                <th style={{ padding: '8px 10px', fontWeight: 600 }}>Embalagem</th>
                <th style={{ padding: '8px 10px', fontWeight: 600, textAlign: 'right' }}>Qtd (últ.)</th>
                <th style={{ padding: '8px 10px', fontWeight: 600, textAlign: 'right' }}>Preço (últ.)</th>
                <th style={{ padding: '8px 10px', fontWeight: 600, whiteSpace: 'nowrap' }}>Última compra</th>
                <th style={{ padding: '8px 10px', fontWeight: 600, textAlign: 'right' }}>Compras</th>
                <th style={{ padding: '8px 10px', fontWeight: 600, textAlign: 'right' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {linhasFiltradas.map((l) => {
                const descPrincipal = l.produto_descricao || l.descricao_nf || '—';
                const descSub = l.produto_descricao && l.descricao_nf && l.descricao_nf !== l.produto_descricao
                  ? l.descricao_nf : null;
                return (
                  <tr key={l.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '6px 10px' }}>
                      {l.produto_foto_url ? (
                        <img
                          src={l.produto_foto_url}
                          alt=""
                          style={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 4, background: '#fff', border: '1px solid var(--border)' }}
                          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                        />
                      ) : (
                        <ImageOff size={16} style={{ color: 'var(--border)' }} />
                      )}
                    </td>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                      <div className="text-gray-500 font-bold no-underline" style={{ fontFamily: 'var(--font-mono, monospace)' }}>
                        {l.produto_codigo_interno || '—'}
                      </div>
                      {l.codigo_fornecedor && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>forn.: {l.codigo_fornecedor}</div>
                      )}
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <div style={{ fontWeight: 600 }}>{descPrincipal}</div>
                      {descSub && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>NF: {descSub}</div>}
                    </td>
                    <td style={{ padding: '8px 10px' }}>{l.marca || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>{l.embalagem || '—'}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'var(--font-mono, monospace)' }}>
                      {fmtQtd(l.ultima_quantidade)}
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, fontFamily: 'var(--font-mono, monospace)' }}>
                      {formatMoeda(l.ultimo_preco ?? 0)}
                    </td>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', fontSize: 12 }}>
                      {l.ultima_data || '—'}
                      {l.ultima_nota_numero && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>NF {l.ultima_nota_numero}</div>
                      )}
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right' }}>{fmtQtd(l.qtd_compras)}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, fontFamily: 'var(--font-mono, monospace)' }}>
                      {formatMoeda(l.total_gasto ?? 0)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
